/**
 * Courses filtering: value constants for the community courses list surface.
 *
 * Companion files:
 *  - `types/courses-filtering.ts` — types (API contract, sort, grouping, composable return)
 *  - `utils/courses-filtering.ts` — helpers (sanitize, group, rank, resolve sort mode, icon lookup)
 */

import type { Component } from 'vue'

import { BarChart10Icon, Dataflow04Icon, Globe01Icon } from '@gohighlevel/ghl-icons/24/outline'

import type { CourseSortMode, CourseTagCategory } from '../types/courses-filtering'

// ============================================================================
// Sort
// ============================================================================

/**
 * Sort keys accepted by the v2 `user/courses` endpoint. The server enforces its own enum;
 * anything else will 400. Keep this list in lock-step with the backend controller.
 *
 * `_id` is used for the "created-at" sort dimension because a Mongo ObjectId embeds the
 * creation timestamp — the server sorts on `_id` directly rather than a separate `createdAt`
 * field to avoid an extra index. Do not rename this key without a coordinated backend change.
 */
export const CourseSortBy = {
  SEQUENCE: 'sequence',
  TITLE: 'title',
  CREATED_AT: '_id',
} as const

/** Sort direction values accepted by the v2 endpoint. */
export const SortDirection = {
  ASCENDING: 'ascending',
  DESCENDING: 'descending',
} as const

/**
 * The sort modes offered in the courses filter sidebar. The first entry is the server default
 * (sequence-descending) — `hasActiveServerSort` and the "Clear all" affordance both treat it as
 * "no sort applied", so keep it first.
 */
export const COURSE_SORT_MODES: readonly CourseSortMode[] = [
  {
    key: 'sequence-desc',
    sortBy: CourseSortBy.SEQUENCE,
    sort: SortDirection.DESCENDING,
    labelKey: 'courses.filters.sortPresets.recommended',
  },
  {
    key: 'createdAt-desc',
    sortBy: CourseSortBy.CREATED_AT,
    sort: SortDirection.DESCENDING,
    labelKey: 'courses.filters.sortPresets.newest',
  },
  { key: 'createdAt-asc', sortBy: CourseSortBy.CREATED_AT, sort: SortDirection.ASCENDING, labelKey: 'courses.filters.sortPresets.oldest' },
  { key: 'title-asc', sortBy: CourseSortBy.TITLE, sort: SortDirection.ASCENDING, labelKey: 'courses.filters.sortPresets.aToZ' },
  { key: 'title-desc', sortBy: CourseSortBy.TITLE, sort: SortDirection.DESCENDING, labelKey: 'courses.filters.sortPresets.zToA' },
]

// ============================================================================
// Pagination / backend limits
// ============================================================================

export const COURSES_LIST_DEFAULT_PAGE_NO = 1
export const COURSES_LIST_DEFAULT_LIMIT = 20
export const COURSES_LIST_MAX_LIMIT = 100

/** Backend rejects more than 50 tag ids per request. */
export const TAG_IDS_FILTER_MAX_SIZE = 50

/** Backend rejects any tag id that is not a 24-char hex string (`@IsMongoId`). */
export const MONGO_OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i

// ============================================================================
// Tag display (card + sidebar)
// ============================================================================

/**
 * Display order for course-card tag pills. Language is surfaced first because the card only
 * shows a handful of tags and language is the most scannable attribute in that context.
 * Any category not in this list is appended at the end (Array.prototype.sort is stable, so
 * unknown categories keep their original API order).
 */
export const TAG_CATEGORY_ORDER = ['language', 'difficulty', 'topic'] as const

/**
 * Display order for the filter sidebar sections. Topic is surfaced first because it is the
 * primary discovery axis on the courses list. Unknown categories are appended at the end
 * (stable sort preserves API order).
 */
export const FILTER_CATEGORY_ORDER = ['topic', 'difficulty', 'language'] as const

/**
 * Maximum tag pills rendered on a single course card. Extra tags are truncated silently to
 * keep the card height stable regardless of how many tags a course carries.
 */
export const MAX_VISIBLE_CARD_TAGS = 3

/**
 * Category → icon lookup. The backend sends `category` as a free-form string, so callers should
 * match case-insensitively on the known catalog values (Language, Difficulty, Topic).
 * Anything unrecognised falls back to a generic tag icon via `getTagIcon` so we still render legibly.
 */
export const CATEGORY_ICON_MAP: Record<CourseTagCategory, Component> = {
  language: Globe01Icon,
  difficulty: BarChart10Icon,
  topic: Dataflow04Icon,
}

// ============================================================================
// Filter sidebar UI constants
// ============================================================================

/** Debounce (ms) applied between checkbox toggles and the follow-up list refetch.
 * Sized to swallow multi-select bursts (users toggling several tags in a row) into a single
 * request while keeping a single deliberate click feel snappy. 250ms was too tight — a normal
 * click cadence beats it — so bumped to 600ms. */
export const COURSE_TAG_FILTER_DEBOUNCE_MS = 600

/**
 * How many tags a category shows before it collapses behind "Show more". Categories can carry
 * dozens of values (Topic in particular), and an unbounded list pushes every category below it
 * out of the sidebar viewport.
 */
export const CATEGORY_VISIBLE_TAG_LIMIT = 10
